import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Edit, Trash2, ChevronUp, ChevronDown, Save, Home } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { Button } from '../../components';
import { projectsService } from '../../services';

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ProjectList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ProjectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: ${props => props.theme.colors.background.secondary};
  border: 1px solid ${props => props.theme.colors.border?.light || 'rgba(0,0,0,0.1)'};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const OrderControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;

  button {
    background: none;
    border: 1px solid ${props => props.theme.colors.border?.light || 'rgba(0,0,0,0.15)'};
    border-radius: 4px;
    cursor: pointer;
    padding: 2px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.colors.text.secondary};
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      background: ${props => props.theme.colors.primaryLight};
      color: ${props => props.theme.colors.primary};
      border-color: ${props => props.theme.colors.primary};
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
`;

const ProjectThumb = styled.div`
  width: 56px;
  height: 40px;
  border-radius: 6px;
  background-color: ${props => props.theme.colors.background.tertiary};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const ProjectInfo = styled.div`
  flex: 1;
  min-width: 0;

  .title {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    font-size: ${props => props.theme.typography.body};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .description {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.small};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 2px;
  }

  .tech-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
  }

  .tech-tag {
    background: ${props => props.theme.colors.primaryLight};
    color: ${props => props.theme.colors.primary};
    padding: 1px 6px;
    border-radius: 4px;
    font-size: ${props => props.theme.typography.xs};
    font-weight: 500;
  }
`;

const HomepageBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.theme.colors.primary};
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 20px;
  flex-shrink: 0;
  white-space: nowrap;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const SaveBar = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 100;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};

  h3 {
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 2rem;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const HOMEPAGE_SLOTS = 3;

export const ProjectsAdmin = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await projectsService.getAllProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const move = (index, direction) => {
    const next = [...projects];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= next.length) return;
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    setProjects(next);
    setIsDirty(true);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      await projectsService.updateProjectsOrder(projects.map(p => p.id));
      setIsDirty(false);
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Failed to save order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await projectsService.deleteProject(id);
        setProjects(projects.filter(p => p.id !== id));
        setIsDirty(true);
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Failed to delete project');
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Projects">
        <LoadingContainer>
          <p>Loading projects...</p>
        </LoadingContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Projects">
      <HeaderActions>
        <HeaderLeft>
          <h2 style={{ margin: 0, color: 'var(--color-text-primary)' }}>
            All Projects ({projects.length})
          </h2>
          <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
            Top {HOMEPAGE_SLOTS} shown on homepage
          </span>
        </HeaderLeft>
        <Button as={Link} to="/admin/projects/new">
          <Plus size={18} />
          New Project
        </Button>
      </HeaderActions>

      {projects.length === 0 ? (
        <EmptyState>
          <h3>No projects yet</h3>
          <p>Start showcasing your work by creating your first project.</p>
          <Button as={Link} to="/admin/projects/new" size="lg">
            <Plus size={20} />
            Create Your First Project
          </Button>
        </EmptyState>
      ) : (
        <ProjectList>
          {projects.map((project, index) => (
            <ProjectRow key={project.id}>
              <OrderControls>
                <button
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  title="Move up"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  onClick={() => move(index, 1)}
                  disabled={index === projects.length - 1}
                  title="Move down"
                >
                  <ChevronDown size={14} />
                </button>
              </OrderControls>

              <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)', width: '20px', textAlign: 'right', flexShrink: 0 }}>
                {index + 1}
              </span>

              {project.featuredImage && (
                <ProjectThumb image={project.featuredImage} />
              )}

              <ProjectInfo>
                <div className="title">{project.title}</div>
                {project.description && (
                  <div className="description">{project.description}</div>
                )}
                {project.technologies?.length > 0 && (
                  <div className="tech-tags">
                    {project.technologies.slice(0, 4).map(tech => (
                      <span key={tech} className="tech-tag">{tech}</span>
                    ))}
                    {project.technologies.length > 4 && (
                      <span className="tech-tag">+{project.technologies.length - 4}</span>
                    )}
                  </div>
                )}
              </ProjectInfo>

              {index < HOMEPAGE_SLOTS && (
                <HomepageBadge>
                  <Home size={11} />
                  Homepage
                </HomepageBadge>
              )}

              <RowActions>
                <Button
                  as={Link}
                  to={`/admin/projects/${project.id}/edit`}
                  variant="outline"
                  size="sm"
                >
                  <Edit size={14} />
                  Edit
                </Button>
                <Button
                  onClick={() => handleDelete(project.id)}
                  variant="ghost"
                  size="sm"
                  style={{ color: 'var(--color-error)' }}
                >
                  <Trash2 size={14} />
                </Button>
              </RowActions>
            </ProjectRow>
          ))}
        </ProjectList>
      )}

      {isDirty && (
        <SaveBar>
          <Button onClick={handleSaveOrder} disabled={saving} size="lg">
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Order'}
          </Button>
        </SaveBar>
      )}
    </AdminLayout>
  );
};
